import { Injectable, PLATFORM_ID, Inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { isPlatformServer } from '@angular/common';
import { map, catchError } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { Lang } from '../models/lang';
import { CarDetailResponse, CarDetail, CarListResponse, CarListItem, CarCollection, CarPagination } from '../models/car';
import { SortRequest } from '../requests/sort-request';

@Injectable({
  providedIn: 'root'
})
export class CarService {

  listItems: CarListItem[] = [];
  lastSortRequest: SortRequest;
  lastPagination: CarPagination;

  // 詳細画面から戻った際の一覧アンカー位置保持用
  lastListPageOffsetY = 0.0;
  lastListPagePath = '';

  constructor(
    private http: HttpClient,
    @Inject(PLATFORM_ID) private platformId: object,
  ) { }

  fetchOne(id: string, lang: Lang): Observable<CarDetail> {
    const apiUrl = this.getApiHost() + `/api/cars/${id}?lang=${lang}`;
    return this.http.get<CarDetailResponse>(apiUrl)
    .pipe(
      map(response => {
        return response.data;
      }),
      catchError(error => {
        console.log(error);
        return of(error);
      })
    );
  }

  fetchAll(lang: Lang, sortRequest: SortRequest): Observable<CarCollection> {
    if (!this.shouldFetchAll(sortRequest)) {
      return of({
        items: this.listItems,
        pagination: this.lastPagination,
      });
    }
    const queryParams = this.getQueryParams(sortRequest);
    queryParams.unshift(`lang=${lang}`);
    queryParams.push('limit=24');
    const apiUrl = this.getApiHost() + `/api/cars?${queryParams.join('&')}`;
    return this.http.get<CarListResponse>(apiUrl)
      .pipe(
        map(response => {
          this.listItems = response.data;
          this.lastPagination = response.pagination;
          this.lastSortRequest = sortRequest;
          return {
            items: response.data,
            pagination: response.pagination,
          };
        }),
        catchError(error => {
          console.log(error);
          return of(error);
        })
      );
  }

  private shouldFetchAll(sortRequest: SortRequest) {
    if (!this.lastSortRequest || this.listItems.length === 0) {
      return true;
    }
    if (!this.isSameLastRequestAll(sortRequest)) {
      return true;
    }
    return false;
  }

  private isSameLastRequestAll(sortRequest: SortRequest) {
    return this.lastSortRequest.selling === sortRequest.selling
      && this.lastSortRequest.brandId === sortRequest.brandId
      && this.lastSortRequest.page === sortRequest.page
      && this.listItems.length > 0;
  }

  private getQueryParams(sortRequest: SortRequest) {
    const params: string[] = [];
    if (sortRequest.selling) {
      params.push('selling=1');
    }
    if (sortRequest.brandId) {
      params.push('brand_id=' + sortRequest.brandId);
    }
    if (sortRequest.page) {
      params.push('page=' + sortRequest.page);
    }
    return params;
  }

  private getApiHost() {
    // SSRの場合、localhostにする
    if (isPlatformServer(this.platformId)) {
      return 'http://localhost';
    }
    return environment.apiHost;
  }

  fetchRanking(lang: Lang): Observable<CarCollection> {
    const apiUrl = this.getApiHost() + `/api/ranking?lang=${lang}`;
    return this.http.get<CarListResponse>(apiUrl)
      .pipe(
        map(response => {
          return {
            items: response.data,
            pagination: response.pagination,
          };
        }),
        catchError(error => {
          console.log(error);
          return of(error);
        })
      );
  }

}
